// English translations
export const en = {
  // Navigation
  nav: {
    home: 'Home',
    services: 'Services',
    forPartners: 'For Partners',
    aboutUs: 'About Us',
    contact: 'Contact',
    bookStrategyCall: 'Book Strategy Call',
  },

  // Hero Section
  hero: {
    badge: 'Your growth — Our responsibility',
    title: 'Lead Generation',
    titleHighlight: 'Company',
    description:
      'We deliver high-quality, GDPR-compliant leads through premium traffic funnels and performance channels.',
    getStarted: 'Get Started',
    exploreServices: 'Explore Services',
    trustedBy: 'Нам доверяют более 50+ компаний',
  },

  // Stats
  stats: {
    leadsGenerated: 'Availability for Our Clients',
    withPartners: 'With our Partners',
    campaignsDelivered: 'Campaigns Delivered',
    emailDeliverability: 'Email Deliverability',
    gdprCompliant: 'GDPR Compliant',
  },

  // Partners Section
  partners: {
    title: 'Our Ad Platform Partners',
  },

  // Live Counter
  liveCounter: {
    badge: 'Live Metrics',
    title: 'Advertising Investment',
    updating: 'Updating in real-time',
    description:
      'Dynamically calculated based on our daily advertising investments with trusted partners.',
    dailyInvestment: 'Daily Investment',
    perSecond: 'Per Second',
    activeCampaigns: 'Active Campaigns',
  },

  // Services Section
  services: {
    badge: 'Our Services',
    title: 'Premium Lead Generation Solutions',
    subtitle: 'End-to-end performance marketing services tailored for market success.',
    viewAll: 'View All Services',
    items: {
      coReg: {
        title: 'Co-Registration Leads',
        shortDesc:
          'High-converting multi-step registration flows that capture qualified user intent.',
        fullDesc:
          'Our co-registration funnels are designed to maximize conversion while ensuring data quality. Users opt-in to receive information from your brand during their registration journey, providing genuine interest signals.',
      },
      coSpo: {
        title: 'Co-Sponsoring Leads',
        shortDesc: 'Premium placements in partner registration flows for maximum visibility.',
        fullDesc:
          'Leverage our network of premium publisher partners to reach highly engaged audiences. Sponsored placements ensure your brand appears prominently during user registration.',
      },
      email: {
        title: 'Email Marketing',
        shortDesc: 'powered campaigns with industry-leading deliverability rates',
        fullDesc: 'powered campaigns with industry-leading deliverability rates',
      },
      sms: {
        title: 'SMS DOI Funnels',
        shortDesc: 'Double opt-in SMS campaigns with real-time delivery and tracking.',
        fullDesc:
          'Reach customers directly on their mobile devices with compliant SMS marketing. Our DOI process ensures genuine consent and high engagement rates.',
      },
      leadAcquisition: {
        title: 'Lead Acquisition',
        shortDesc: 'Engaging campaigns that drive massive qualified lead volume.',
        fullDesc:
          'Our lead acquisition campaigns combine attractive offers with strategic targeting to generate high volumes of qualified leads while maintaining data quality.',
      },
      phone: {
        title: 'Phone Lead Delivery',
        shortDesc: 'Real-time validated phone leads with guaranteed contact rates.',
        fullDesc:
          'We deliver phone-verified leads with real-time validation to ensure accuracy. Direct integration with your CRM for seamless lead flow.',
      },
      data: {
        title: 'Data Monetization & Segmentation',
        shortDesc: 'Advanced audience segmentation for precision targeting.',
        fullDesc:
          'Transform raw data into actionable segments. Our advanced analytics identify high-value audience clusters for maximum campaign effectiveness.',
      },
      api: {
        title: 'API Delivery & Real-time Validation',
        shortDesc: 'Seamless integration with instant lead delivery and verification.',
        fullDesc:
          'Our robust API infrastructure enables real-time lead delivery with instant validation. Reduce fraud and improve data quality automatically.',
      },
    },
  },

  // USPs Section
  usps: {
    badge: 'Why Choose Us',
    title: 'Why Avalon X Media?',
    subtitle: 'The competitive advantages that set us apart in lead generation.',
    tagline: 'Мы создаем то, что другие продают. Your trusted source.',
    items: {
      directSource: {
        title: 'Direct Source',
        description: '100% first-party leads from our proprietary traffic network.',
      },
      gdprCompliant: {
        title: 'GDPR Compliant',
        description: 'Full regulatory compliance with transparent consent management.',
      },
      highDeliverability: {
        title: 'High Deliverability',
        description: 'Industry-leading email delivery rates powered by AWS SES.',
      },
      realTimeValidation: {
        title: 'Real-time Validation',
        description: 'Instant verification ensures data accuracy and reduces fraud.',
      },
      customFunnels: {
        title: 'Custom Funnels',
        description: 'Tailored acquisition flows for your specific vertical and goals.',
      },
      transparentReporting: {
        title: 'Transparent Reporting',
        description: 'Real-time dashboards with full campaign visibility.',
      },
    },
  },

  // Verticals Section
  verticals: {
    badge: 'Industry Expertise',
    title: 'Custom Funnels for Every Product',
    subtitle:
      'We specialize in high-performance lead generation across world most competitive industries.',
    items: ['Finance', 'Insurance', 'E-commerce', 'Retail', 'Mobility', 'FMCG'],
    metrics: {
      conversionRate: 'Avg. Conversion Rate',
      avgCpl: 'Avg. CPL',
      emailDeliverability: 'Email Deliverability',
      reachPerMonth: 'Reach per Month',
    },
  },

  // Compliance Section
  compliance: {
    badge: 'Compliance & Trust',
    title: '100% GDPR Compliant',
    subtitle:
      'We take data protection seriously. Every lead is collected with explicit consent and full regulatory compliance.',
    items: [
      'Transparent consent management',
      'Real-time data validation',
      'Complete audit trail documentation',
      'Regular compliance audits',
      'Data Processing Agreement (DPA) ready',
    ],
    cards: {
      gdpr: { label: 'GDPR Compliant', desc: 'Full EU data protection' },
      doi: { label: 'DOI Verified', desc: 'Double opt-in process' },
      secure: { label: 'Secure Data', desc: 'Encrypted transmission' },
      documented: { label: 'Documented Consent', desc: 'Complete audit trail' },
    },
  },

  // CTA Section
  cta: {
    title: 'Ready to Scale Your Lead Generation?',
    subtitle:
      "Let's discuss how Avalon X Media can help you acquire high-quality, GDPR-compliant leads for your business.",
    requestLeadFlow: 'Request Lead Flow',
    learnMore: 'Learn More',
  },

  // Services Page
  servicesPage: {
    badge: 'Our Services',
    title: 'Full-Spectrum Lead Generation Services',
    subtitle:
      'From co-registration funnels to real-time API delivery, we provide end-to-end solutions for advertisers seeking quality leads.',
    process: {
      badge: 'Our Process',
      title: 'How We Work',
      subtitle: 'A streamlined approach to delivering results for your campaigns.',
      steps: [
        {
          step: '01',
          title: 'Discovery',
          desc: 'We analyze your target audience and campaign objectives.',
        },
        { step: '02', title: 'Strategy', desc: 'Custom funnel design and channel selection.' },
        { step: '03', title: 'Launch', desc: 'Campaign deployment with real-time monitoring.' },
        {
          step: '04',
          title: 'Optimize',
          desc: 'Continuous improvement based on performance data.',
        },
      ],
    },
    ctaTitle: 'Need a Custom Solution?',
    ctaSubtitle:
      'Our team can build tailored lead generation strategies specific to your industry and goals.',
    requestQuote: 'Request Custom Quote',
  },

  // For Partners Page
  partnersPage: {
    badge: 'For Partners',
    title: 'High-Quality Leads,',
    titleHighlight: 'Zero Compromise',
    subtitle:
      'Partner with leading performance marketing agency for GDPR-compliant, high-converting leads delivered on your terms.',
    requestLeadFlow: 'Request Lead Flow',
    viewServices: 'View Services',
    benefits: {
      badge: 'Partner Benefits',
      title: 'Why Partner With Us',
      subtitle: 'Everything you need to scale your customer acquisition efficiently.',
      items: {
        gdpr: {
          title: '100% GDPR-Compliant Leads',
          description:
            'Every lead is collected with explicit, documented consent meeting and EU data protection standards.',
        },
        filters: {
          title: 'Custom Traffic Filters',
          description:
            "Define your ideal customer profile and we'll filter traffic to match your exact specifications.",
        },
        billing: {
          title: 'CPL-Based Billing',
          description:
            'Pay only for qualified leads that meet your criteria. No hidden fees, complete transparency.',
        },
        quality: {
          title: 'Quality Control & Deduplication',
          description:
            'Advanced algorithms ensure lead freshness and eliminate duplicates across campaigns.',
        },
        dashboard: {
          title: 'Real-time Dashboard Reporting',
          description:
            'Monitor your campaigns 24/7 with detailed analytics and performance metrics.',
        },
        account: {
          title: 'Dedicated Account Management',
          description:
            'Work directly with experienced professionals who understand your business goals.',
        },
      },
    },
    howItWorks: {
      badge: 'Getting Started',
      title: 'Start in 3 Simple Steps',
      subtitle: 'From initial contact to live leads in as little as 7 days.',
      steps: [
        {
          title: 'Define Your Criteria',
          desc: 'Tell us about your ideal customer profile, geographic targeting, and quality requirements.',
        },
        {
          title: 'Custom Setup',
          desc: 'We build tailored funnels and configure delivery to match your CRM integration needs.',
        },
        {
          title: 'Receive Qualified Leads',
          desc: 'Start receiving real-time, validated leads with full transparency and reporting.',
        },
      ],
    },
    faq: {
      badge: 'FAQs',
      title: 'Common Questions',
      items: [
        {
          q: 'What is your minimum order volume?',
          a: 'We typically work with advertisers seeking 1,000+ leads per month, but can accommodate smaller volumes for testing.',
        },
        {
          q: 'How quickly can we start receiving leads?',
          a: 'Most campaigns go live within 5-7 business days after contract signing and technical setup.',
        },
        {
          q: 'What billing models do you offer?',
          a: 'We primarily work on CPL (Cost Per Lead) basis, with options for CPA arrangements for qualified partners.',
        },
        {
          q: 'How do you ensure GDPR compliance?',
          a: 'All leads are collected with explicit, documented consent through DOI processes, with full audit trails available.',
        },
      ],
    },
    ctaTitle: 'Ready to Get Qualified Leads?',
    ctaSubtitle: 'Request a custom lead flow proposal tailored to your business needs.',
  },

  // Growth Chart
  growthChart: {
    title: 'See Your Growth Trajectory With Avalon X Media',
    subtitle: 'Compare your potential growth with and without our performance marketing expertise.',
    withoutAvalon: 'Without Avalon (3-7% monthly)',
    withAvalon: 'With Avalon (11-24% monthly)',
    fasterGrowth: '+240% Faster',
    growthWithAvalon: 'Growth With Avalon',
    description:
      'Your business accelerates faster with optimized, GDPR-compliant performance marketing.',
  },

  // About Page
  aboutPage: {
    badge: 'About Us',
    title: 'Your Performance Marketing Partner',
    titleHighlight: '',
    subtitle:
      'Avalon X Media GmbH is a performance-driven lead generation partner based in Stuttgart. We combine cutting-edge technology with deep market expertise to deliver measurable results partners.',
    story: {
      badge: 'Our Story',
      title: 'Excellence, Integrity, Partnership',
      paragraphs: [
        'Avalon X Media was founded on a simple insight: companies needed a reliable partner who truly understands the local market’s strict requirements for data quality and regulatory compliance.',
        'We built our company on three core pillars: technological excellence, regulatory integrity, and long-term partnership. Our accelerated growth is fueled by a team with deep, specialized experience in lead generation—united by a shared mission to strengthen and elevate our clients’ brands in the market.',
        'Our proprietary technology stack — including advanced validation systems and in-house platform infrastructure — ensures that every lead meets the highest quality standards while maintaining industry-leading deliverability rates and full GDPR compliance.',
      ],
    },
    values: {
      badge: 'Our Values',
      title: 'What Drives Us',
      subtitle: 'The principles that guide every decision we make.',
      items: [
        {
          title: 'Performance First',
          desc: 'Every campaign is optimized for measurable results and ROI.',
        },
        {
          title: 'Data Ethics',
          desc: 'Unwavering commitment to privacy, consent, and compliance.',
        },
        {
          title: 'Innovation',
          desc: 'Continuous investment in technology and process improvement.',
        },
        { title: 'Partnership', desc: 'Long-term relationships built on transparency and trust.' },
      ],
    },
    team: {
      badge: 'Our Team',
      title: 'Meet the Leadership',
      subtitle: 'Experienced professionals driving results for our partners.',
    },
    location: {
      badge: 'Location',
      title: 'Based in Stuttgart',
      subtitle: 'In the heart of innovation hub, perfectly positioned to serve the market.',
    },
    ctaTitle: "Let's Build Something Great Together",
    ctaSubtitle: 'Ready to scale your lead generation with a trusted  partner?',
    getInTouch: 'Get In Touch',
  },

  // Contact Page
  contactPage: {
    badge: 'Contact Us',
    title: "Let's Start a Conversation",
    subtitle:
      "Ready to scale your lead generation? Get in touch and we'll show you how we can help grow your business.",
    getInTouch: 'Get In Touch',
    office: 'Office',
    email: 'Email',
    phone: 'Phone',
    businessHours: 'Business Hours',
    businessHoursValue: 'Mon - Fri: 9:00 - 18:00 CET',
    bookCall: {
      title: 'Book a Strategy Call',
      description: 'Schedule a 30-minute call to discuss your lead generation needs.',
      button: 'Schedule Call',
    },
    form: {
      fullName: 'Full Name',
      emailAddress: 'Email Address',
      company: 'Company',
      phoneNumber: 'Phone Number',
      subject: 'Subject',
      expectedLeads: 'Expected Leads/Month',
      message: 'Message',
      messagePlaceholder: 'Tell us about your lead generation needs...',
      privacyNote: 'By submitting, you agree to our privacy policy.',
      sendMessage: 'Send Message',
      sending: 'Sending...',
      successTitle: 'Message Sent!',
      successMessage: "Thank you for reaching out. We'll get back to you within 24 hours.",
    },
  },

  // Imprint Page
  imprintPage: {
    title: 'Imprint',
    subtitle: 'Information according to § 5 TMG',
    backToHome: 'Back to Home',
    managingDirector: 'Represented by the Managing Director',
    commercialRegister: 'Commercial Register Entry',
    courtOfRegistration: 'Court of Registration',
    registrationNumber: 'Registration Number',
    vatId: 'VAT ID',
    contactTitle: 'Contact',
  },

  // Privacy Page
  privacyPage: {
    title: 'Privacy Policy',
    subtitle: 'Datenschutzerklärung',
    backToHome: 'Back to Home',
    lastUpdated: 'Last updated: May 2025',
    sections: {
      controller: 'Controller',
      generalInfo: 'General Information on Data Processing',
      generalInfoText:
        'We take the protection of your personal data very seriously. The collection and processing of your data is carried out exclusively in accordance with legal requirements, particularly the GDPR and the Federal Data Protection Act (BDSG).',
      purposes: 'Purposes and Legal Basis of Data Processing',
      purposesIntro: 'Your data may be processed based on the following legal grounds:',
      purposesList: [
        'Performance of contracts or pre-contractual measures (Art. 6(1)(b) GDPR)',
        'Compliance with legal obligations (Art. 6(1)(c) GDPR)',
        'Protection of legitimate interests (Art. 6(1)(f) GDPR)',
        'Consent of the data subject (Art. 6(1)(a) GDPR)',
      ],
      collection: 'Collection and Storage of Personal Data',
      collectionIntro:
        'We collect and store personal data when you submit it through inquiries, registrations, or in relation to contract execution. This includes:',
      collectionList: [
        'Name',
        'Address',
        'Email address',
        'Phone number',
        'Payment information',
        'IP address',
      ],
      disclosure: 'Disclosure of Data to Third Parties',
      disclosureIntro: 'Data is transferred to third parties only if:',
      disclosureList: [
        'You have given your consent',
        'It is necessary for contract performance',
        'We are legally obligated',
        'A legitimate interest exists',
      ],
      rights: 'Rights of Data Subjects',
      rightsIntro: 'You have the following rights under GDPR:',
      rightsList: [
        'Right to access',
        'Right to rectification',
        'Right to erasure',
        'Right to restriction of processing',
        'Right to data portability',
        'Right to object',
        'Right to withdraw consent',
      ],
      retention: 'Data Retention',
      retentionText:
        'Data is stored only for as long as required for its intended purpose or for legally mandated retention periods.',
      security: 'Data Security',
      securityText:
        'We implement appropriate technical and organizational measures to protect your data from unauthorized access, loss, or destruction.',
      updates: 'Updates and Changes',
      updatesText:
        'This Privacy Policy reflects the status as of May 2025. Future updates will be published at:',
    },
  },

  // Footer
  footer: {
    company: 'Company',
    servicesTitle: 'Services',
    legal: 'Legal',
    privacyPolicy: 'Privacy Policy',
    imprint: 'Imprint',
    termsOfService: 'Terms of Service',
    allRightsReserved: 'All rights reserved.',
  },
};
